/**
 * Status Reporting settings: each month's reporting window, the board date
 * rules that suggest its dates, and per-project extensions.
 *
 * Reachable by status reporting admins as well as platform admins, which is
 * why it is its own page rather than a card on the admin-only page.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle, CalendarDays, ChevronDown, CornerDownRight, Loader2, Plus, RefreshCw, Save, Send, ShieldCheck, Trash2,
} from 'lucide-react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../hooks/useAuth'
import { useData } from '../hooks/useData'
import LoadingSpinner from '../components/LoadingSpinner'
import Modal from '../components/Modal'
import SettingsTabs from '../components/SettingsTabs'
import { inputClass } from '../components/Field'
import { currentReportMonth, monthLabel, shiftMonth } from '../lib/report'
import {
  DEADLINE_HOUR, MAX_WORKING_DAYS, applyBoardDate, applyRule, draftFromWindow, isFullDate, moveDate, offeredRuleDate,
  rulesProblem, windowProblem, workingDaysFor,
} from '../lib/boardDates'
import type { BoardDateDraft, Deadline } from '../lib/boardDates'
import { fmtUk, fromLocalInputs, parseInstant, resolveWindow, toLocalInputs, windowChipLabel } from '../lib/reportingWindow'
import type {
  BoardDateRules, BoardDateRulesUpdate, MonthReview, Project, ReportingWindowUpdate, Settings as SettingsType,
  WindowSaveResult,
} from '../types'

// Two months back (to grant a late extension) through three ahead.
const MONTH_OFFSETS = [-2, -1, 0, 1, 2, 3]

const DEADLINE_TIME = `${String(DEADLINE_HOUR).padStart(2, '0')}:00`

const fieldLabel = 'text-xs font-semibold text-slate-500 uppercase'
const primaryButton = 'bg-[#005EB8] text-white px-3.5 py-2 rounded-lg text-sm font-semibold hover:bg-[#004a94] flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap'

/** "Thu 29 Oct": the month is on the row, so the year is left off. */
const dayLabel = (date: string) => fmtUk(parseInstant(date), false, false)

interface WindowDraft extends BoardDateDraft {
  note: string
}

const EMPTY_DRAFT: WindowDraft = { boardDate: '', dueDate: '', lockDate: '', dueMoved: false, lockMoved: false, note: '' }

function draftFrom(rules: BoardDateRulesUpdate, review?: MonthReview): WindowDraft {
  const w = review?.reportingWindow
  return w ? { ...draftFromWindow(w, rules), note: w.note ?? '' } : EMPTY_DRAFT
}

const sameDraft = (a: WindowDraft, b: WindowDraft) =>
  (Object.keys(EMPTY_DRAFT) as (keyof WindowDraft)[]).every(key => a[key] === b[key])

function WindowChip({ month, review, projectId }: { month: string; review?: MonthReview; projectId?: string }) {
  const s = resolveWindow(month, review, projectId)
  const tone = s.pastDue
    ? 'bg-[#F88806]/10 text-[#B8640B] border-[#F88806]/30'
    : s.isOpen
      ? 'bg-[#00ACED]/10 text-[#0077A8] border-[#00ACED]/30'
      : s.notYetOpen || s.dueAt ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-[#F88806]/10 text-[#B8640B] border-[#F88806]/30'
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${tone}`}>
      <CalendarDays className="w-3 h-3" />
      {windowChipLabel(s)}
      {s.source === 'default' && <span className="font-normal opacity-70">(default rule)</span>}
    </span>
  )
}

function DateField({ id, month, label, value, onChange, withTime = false, children }: {
  id: string
  month: string
  label: string
  value: string
  onChange: (date: string) => void
  withTime?: boolean
  children?: ReactNode
}) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={`block ${fieldLabel}`}>
        <span className="sr-only">{monthLabel(month)} </span>{label}
      </label>
      <div className="flex items-center gap-2 mt-1">
        <input id={id} type="date" value={value} onChange={e => onChange(e.target.value)}
               className={`${inputClass} min-w-0 tabular-nums`} />
        {withTime && <span className="text-sm text-slate-500">{DEADLINE_TIME}</span>}
      </div>
      {isFullDate(value) && <div className="mt-1 text-xs text-slate-500">{dayLabel(value)}</div>}
      {children}
    </div>
  )
}

/** Whole numbers only: an empty or fractional count is not a count. */
const parseCount = (value: string) => (/^\d+$/.test(value.trim()) ? Number(value) : Number.NaN)

function BoardDateRulesCard({ rules, onSaved, notify }: {
  rules: BoardDateRules
  onSaved: (rules: BoardDateRules) => void
  notify: (modal: { title: string; message: string }) => void
}) {
  const api = useApi()
  const [due, setDue] = useState(String(rules.dueWorkingDaysBefore))
  const [lock, setLock] = useState(String(rules.lockWorkingDaysBefore))
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setDue(String(rules.dueWorkingDaysBefore))
    setLock(String(rules.lockWorkingDaysBefore))
  }, [rules])

  const draft: BoardDateRulesUpdate = { dueWorkingDaysBefore: parseCount(due), lockWorkingDaysBefore: parseCount(lock) }
  const dirty = draft.dueWorkingDaysBefore !== rules.dueWorkingDaysBefore || draft.lockWorkingDaysBefore !== rules.lockWorkingDaysBefore
  const problem = rulesProblem(draft)

  const save = async () => {
    setSaving(true)
    try {
      onSaved(await api.setBoardDateRules(draft))
      notify({ title: 'Board date rules saved', message: 'Months already saved keep their dates.' })
    } catch (e) {
      notify({ title: 'Save failed', message: e instanceof Error ? e.message : 'Could not save the board date rules.' })
    } finally {
      setSaving(false)
    }
  }

  const rows: [Deadline, string, string, (value: string) => void][] = [
    ['due', 'Reports due', due, setDue],
    ['lock', 'Reports lock', lock, setLock],
  ]

  return (
    <div className="card p-6">
      <h2 className="font-bold text-slate-800 mb-4">Board date rules</h2>
      <div className="space-y-2.5">
        {rows.map(([which, label, value, set]) => (
          <div key={which} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-700">
            <label htmlFor={`rule-${which}`} className="w-24 font-semibold">{label}</label>
            <input id={`rule-${which}`} type="number" min={0} max={MAX_WORKING_DAYS} step={1} inputMode="numeric"
                   value={value} onChange={e => set(e.target.value)} aria-describedby={`rule-${which}-unit`}
                   className="w-16 px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm bg-white tabular-nums" />
            <span id={`rule-${which}-unit`}>working days before the board</span>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3 mt-4">
        {dirty && problem && <span role="status" className="text-xs text-[#B8640B]">{problem}</span>}
        <button type="button" onClick={save} disabled={!dirty || !!problem || saving} className={primaryButton}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>
    </div>
  )
}

export default function SettingsReporting() {
  useDocumentTitle('Status Reporting Settings')
  const api = useApi()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const isAdmin = !!user?.isAdmin
  const isReportingAdmin = !!user?.isReportingAdmin

  const months = useMemo(() => MONTH_OFFSETS.map(d => shiftMonth(currentReportMonth(), d)), [])

  const { data, loading, refetch } = useData(() =>
    Promise.all([
      api.getSettings(),
      api.getProjects(),
      api.getBoardDateRules(),
      Promise.all(months.map(m => api.getMonthReview(m))),
    ]).then(([settings, projects, rules, reviews]) => ({
      settings: settings as SettingsType,
      projects: (projects as Project[]).filter(p => p.reportable).sort((a, b) => a.name.localeCompare(b.name)),
      rules,
      reviews: Object.fromEntries(months.map((m, i) => [m, reviews[i] as MonthReview])) as Record<string, MonthReview>,
    })),
  )

  const [reviews, setReviews] = useState<Record<string, MonthReview>>({})
  const [rules, setRules] = useState<BoardDateRules | null>(null)
  const [drafts, setDrafts] = useState<Record<string, WindowDraft>>({})
  // Each month's draft as loaded or last saved. Kept rather than rebuilt, so a
  // rules change never makes an untouched month look edited.
  const [savedDrafts, setSavedDrafts] = useState<Record<string, WindowDraft>>({})
  const [savingMonth, setSavingMonth] = useState<string | null>(null)
  // Only the current month is open by default; these are the others opened.
  const [openMonths, setOpenMonths] = useState<Set<string>>(new Set())
  const [modal, setModal] = useState<{ title: string; message: string } | null>(null)

  const [extMonth, setExtMonth] = useState(currentReportMonth())
  const [extProject, setExtProject] = useState('')
  const [extDate, setExtDate] = useState('')
  const [extTime, setExtTime] = useState('17:00')
  const [extReason, setExtReason] = useState('')
  const [savingExt, setSavingExt] = useState(false)

  useEffect(() => {
    if (!data) return
    const loaded = Object.fromEntries(months.map(m => [m, draftFrom(data.rules, data.reviews[m])]))
    setReviews(data.reviews)
    setRules(data.rules)
    setDrafts(loaded)
    setSavedDrafts(loaded)
  }, [data, months])

  useEffect(() => {
    if (!authLoading && !isReportingAdmin) navigate('/settings', { replace: true })
  }, [authLoading, isReportingAdmin, navigate])

  const header = (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-slate-500">Configure integrations and defaults</p>
      </div>
      <SettingsTabs active="reporting" />
    </>
  )

  if (authLoading || !isReportingAdmin || loading) return <LoadingSpinner />

  if (!data) {
    return (
      <div>
        {header}
        <div className="card p-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-[#B8640B] shrink-0 mt-0.5" />
          <div>
            <h2 className="font-bold text-slate-800 mb-1">Could not load status reporting settings</h2>
            <p className="text-sm text-slate-500 mb-3">The windows, the board date rules or the project list did not load. Nothing was changed.</p>
            <button type="button" onClick={refetch}
                    className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
              <RefreshCw className="w-3.5 h-3.5" /> Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!rules) return <LoadingSpinner />

  const { settings } = data

  const updateDraft = (month: string, change: (draft: WindowDraft) => WindowDraft) =>
    setDrafts(all => ({ ...all, [month]: change(all[month]) }))

  const saveWindow = async (month: string) => {
    const d = drafts[month]
    if (windowProblem(month, d)) return
    const body: ReportingWindowUpdate = {
      ddiBoardDate: isFullDate(d.boardDate) ? d.boardDate : null,
      dueDate: d.dueDate || null,
      lockDate: d.lockDate,
      dueMoved: d.dueMoved,
      lockMoved: d.lockMoved,
      note: d.note.trim() || null,
    }
    setSavingMonth(month)
    try {
      const result = await api.setReportingWindow(month, body) as WindowSaveResult
      const saved = draftFrom(rules, result.review)
      setReviews(r => ({ ...r, [month]: result.review }))
      setDrafts(dr => ({ ...dr, [month]: saved }))
      setSavedDrafts(dr => ({ ...dr, [month]: saved }))
      setModal({
        title: result.emailSent ? 'Window saved and announced' : 'Window saved, announcement not sent',
        message: `${monthLabel(month)}: ${result.emailDetail ?? ''}`,
      })
    } catch (e) {
      setModal({ title: 'Save failed', message: e instanceof Error ? e.message : 'Could not save the window.' })
    } finally {
      setSavingMonth(null)
    }
  }

  const monthClose = resolveWindow(extMonth, reviews[extMonth]).closesAt
  const extInstant = extDate ? new Date(fromLocalInputs(extDate, extTime)) : null
  const extTooEarly = !!extInstant && extInstant.getTime() <= monthClose.getTime()

  const grantExtension = async () => {
    if (!extProject || !extDate || !extReason.trim() || extTooEarly) return
    setSavingExt(true)
    try {
      const result = await api.setReportingExtension(extMonth, extProject, {
        closesAt: fromLocalInputs(extDate, extTime), reason: extReason.trim(),
      }) as WindowSaveResult
      setReviews(r => ({ ...r, [extMonth]: result.review }))
      setExtReason(''); setExtProject('')
      setModal({ title: result.emailSent ? 'Extension granted' : 'Extension granted, notice not sent', message: result.emailDetail ?? '' })
    } catch (e) {
      setModal({ title: 'Could not grant the extension', message: e instanceof Error ? e.message : 'Unknown error.' })
    } finally {
      setSavingExt(false)
    }
  }

  const revokeExtension = async (month: string, projectId: string) => {
    try {
      const updated = await api.clearReportingExtension(month, projectId) as MonthReview
      setReviews(r => ({ ...r, [month]: updated }))
    } catch (e) {
      setModal({ title: 'Could not revoke the extension', message: e instanceof Error ? e.message : 'Unknown error.' })
    }
  }

  const extReview = reviews[extMonth]
  const extensions = Object.entries(extReview?.extensions ?? {})
  const projectName = (id: string) => data.projects.find(p => p.id === id)?.name ?? id

  return (
    <div>
      {header}

      <Modal open={!!modal} title={modal?.title ?? ''} message={modal?.message ?? ''} onConfirm={() => setModal(null)} />

      <div className="space-y-6">

        {/* ── Monthly windows ─────────────────────────────────────────── */}
        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-1">Reporting windows</h2>
          <p className="text-xs text-slate-500 mb-4">
            When each month's status reports can be submitted, and saving announces it. Months without a
            window stay open to the 7th of the next month.
            {!settings.reminderGroupEmail && (
              <span className="font-semibold text-[#B8640B]">
                {' '}No group address yet, so saving sends nothing ({isAdmin ? 'set one on the Admin tab' : 'ask a platform admin'}).
              </span>
            )}
          </p>
          <div className="space-y-3">
            {(() => { const renderMonth = (month: string) => {
              const review = reviews[month]
              const draft = drafts[month]
              const saved = savedDrafts[month]
              if (!draft || !saved) return null
              const w = review?.reportingWindow
              const edited = !sameDraft(draft, saved)
              // A month with no window yet, or one whose announcement never went
              // out, can always be saved, so the announcement can be sent without
              // a dummy edit. Once announced, only real edits re-announce.
              const dirty = !w?.announcedAt || edited
              const isCurrent = month === currentReportMonth()
              const problem = windowProblem(month, draft)
              // Any other month stays open while it has unsaved edits, so they are never hidden.
              if (!isCurrent && !openMonths.has(month) && !edited) {
                return (
                  <button key={month} type="button" onClick={() => setOpenMonths(o => new Set(o).add(month))}
                          aria-expanded={false}
                          className="w-full rounded-xl border border-slate-200 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap text-left hover:bg-slate-50">
                    <span className="flex items-center gap-2.5 flex-wrap">
                      <span className="font-bold text-slate-600">{monthLabel(month)}</span>
                      <WindowChip month={month} review={review} />
                    </span>
                    <span className="flex items-center gap-2 text-xs text-slate-500">
                      {w
                        ? <>Set by <span className="font-semibold text-slate-700">{w.setBy.name}</span>{!w.announcedAt && <span className="text-[#B8640B] font-semibold"> · not announced</span>}</>
                        : 'No window set'}
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    </span>
                  </button>
                )
              }
              const ruleLink = (which: Deadline) => {
                const ruleDate = offeredRuleDate(draft, which, rules)
                if (!ruleDate) return null
                return (
                  <button type="button" onClick={() => updateDraft(month, d => applyRule(d, which, rules))}
                          className="mt-1 block text-xs font-semibold text-[#005EB8] hover:underline">
                    Use WD-{workingDaysFor(which, rules)} ({dayLabel(ruleDate)})
                    <span className="sr-only"> for {monthLabel(month)} reports {which}</span>
                  </button>
                )
              }
              return (
                <div key={month} className={`rounded-xl border p-4 ${isCurrent ? 'border-danone-cyan/40 bg-[#00ACED]/[0.03]' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <span className="font-bold text-slate-800">{monthLabel(month)}</span>
                      {isCurrent && <span className="text-[10px] uppercase tracking-wide font-bold text-[#0077A8]">current</span>}
                      <WindowChip month={month} review={review} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {w
                        ? <span>Set by <span className="font-semibold text-slate-700">{w.setBy.name}</span> · {fmtUk(new Date(w.setAt))} · {w.announcedAt ? 'announced' : <span className="text-[#B8640B] font-semibold">not announced</span>}</span>
                        : 'No window set'}
                      {!isCurrent && !edited && (
                        <button type="button" aria-label={`Collapse ${monthLabel(month)}`} aria-expanded
                                onClick={() => setOpenMonths(o => { const next = new Set(o); next.delete(month); return next })}
                                className="text-slate-400 hover:text-slate-600">
                          <ChevronDown className="w-4 h-4 rotate-180" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* One row only from lg: the side navigation takes a fixed share of the width. */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <DateField id={`board-${month}`} month={month} label="DDI Board date" value={draft.boardDate}
                               onChange={date => updateDraft(month, d => applyBoardDate(d, date, rules))} />
                    <DateField id={`due-${month}`} month={month} label="Reports due" value={draft.dueDate} withTime
                               onChange={date => updateDraft(month, d => moveDate(d, 'due', date))}>
                      {ruleLink('due')}
                    </DateField>
                    <DateField id={`lock-${month}`} month={month} label="Reports lock" value={draft.lockDate} withTime
                               onChange={date => updateDraft(month, d => moveDate(d, 'lock', date))}>
                      {ruleLink('lock')}
                    </DateField>
                  </div>

                  <div className="flex flex-wrap items-end gap-3 mt-3">
                    <label className={`flex-[1_1_320px] ${fieldLabel}`}>
                      <span className="sr-only">{monthLabel(month)} </span>Note for the email (optional)
                      <input type="text" className={`${inputClass} mt-1 normal-case font-normal`} value={draft.note} placeholder="e.g. Board moved a day for the site visit"
                             onChange={e => { const note = e.target.value; updateDraft(month, d => ({ ...d, note })) }} />
                    </label>
                    <button type="button" onClick={() => saveWindow(month)} disabled={!dirty || !!problem || savingMonth === month} className={primaryButton}>
                      {savingMonth === month ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      {w?.announcedAt ? 'Save and re-announce' : 'Save and announce'}
                      <span className="sr-only"> {monthLabel(month)}</span>
                    </button>
                  </div>
                  {/* Only once the row is touched: an untouched month with no window is not an error. */}
                  {edited && problem && <div role="status" className="mt-2 text-xs text-[#B8640B]">{problem}</div>}
                </div>
              )
            }
            // The current month open at the top, then upcoming months, then
            // previous months (most recent first), both collapsed.
            const current = currentReportMonth()
            const upcoming = months.filter(m => m > current)
            const past = months.filter(m => m < current).reverse()
            const heading = (text: string) => (
              <div className="pt-2 text-[11px] font-bold uppercase tracking-wide text-slate-400">{text}</div>
            )
            return (
              <>
                {months.filter(m => m === current).map(renderMonth)}
                {upcoming.length > 0 && heading('Upcoming months')}
                {upcoming.map(renderMonth)}
                {past.length > 0 && heading('Previous months')}
                {past.map(renderMonth)}
              </>
            )
            })()}
          </div>
        </div>

        {/* ── Extensions ──────────────────────────────────────────────── */}
        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-1">Extensions</h2>
          <p className="text-xs text-slate-500 mb-4">
            Give one project a later close for a month. The project manager is emailed; nobody else is.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-[150px_1fr_1fr_110px] gap-3 items-end mb-3">
            <label className="text-xs font-semibold text-slate-500 uppercase">Month
              <select className={`${inputClass} mt-1 normal-case font-normal`} value={extMonth}
                      onChange={e => { setExtMonth(e.target.value); setExtDate('') }}>
                {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-500 uppercase">Project
              <select className={`${inputClass} mt-1 normal-case font-normal`} value={extProject} onChange={e => setExtProject(e.target.value)}
                      disabled={data.projects.length === 0}>
                <option value="">{data.projects.length ? 'Select a reportable project' : 'No reportable projects yet'}</option>
                {data.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="text-xs font-semibold text-slate-500 uppercase">New close
              <input type="date" className={`${inputClass} mt-1 normal-case font-normal`} value={extDate}
                     min={toLocalInputs(monthClose.toISOString()).date}
                     onChange={e => setExtDate(e.target.value)} />
            </label>
            <label className="text-xs font-semibold text-slate-500 uppercase">Time
              <input type="time" className={`${inputClass} mt-1 normal-case font-normal`} value={extTime} onChange={e => setExtTime(e.target.value)} />
            </label>
          </div>
          {extTooEarly && (
            <div className="text-xs text-[#B8640B] mb-2">An extension must close after the month's close, {fmtUk(monthClose)}.</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <label className="text-xs font-semibold text-slate-500 uppercase">Reason (goes in the email)
              <input type="text" className={`${inputClass} mt-1 normal-case font-normal`} value={extReason} placeholder="e.g. Site audit week, agreed with the PM"
                     onChange={e => setExtReason(e.target.value)} />
            </label>
            <button type="button" onClick={grantExtension} disabled={savingExt || !extProject || !extDate || !extReason.trim() || extTooEarly}
                    title={extTooEarly ? `Must close after the month's close, ${fmtUk(monthClose)}` : undefined}
                    className={primaryButton}>
              {savingExt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Grant extension
            </button>
          </div>

          <div className="mt-4 border-t border-slate-100 pt-3 space-y-2">
            <div className="text-xs font-semibold text-slate-500 uppercase">Active for {monthLabel(extMonth)}</div>
            {extensions.length === 0 && <div className="text-sm text-slate-400 italic">No extensions for this month.</div>}
            {extensions.map(([projectId, ext]) => (
              <div key={projectId} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2">
                <div className="text-sm">
                  <span className="font-semibold text-slate-800">{projectName(projectId)}</span>
                  <span className="text-slate-500"> · extended to </span>
                  <span className="font-semibold text-[#0077A8]">{fmtUk(new Date(ext.closesAt))}</span>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-start gap-1">
                    <CornerDownRight className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{ext.reason} <span className="text-slate-400">· by {ext.grantedBy.name}{ext.notifiedAt ? ', PM notified' : ', PM not notified'}</span></span>
                  </div>
                </div>
                <button type="button" onClick={() => revokeExtension(extMonth, projectId)} title="Revoke this extension"
                        aria-label={`Revoke the extension for ${projectName(projectId)}`}
                        className="text-slate-400 hover:text-[#B8242E] p-1 shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ── Board date rules ────────────────────────────────────────── */}
        <BoardDateRulesCard rules={rules} onSaved={setRules} notify={setModal} />

        <p className="flex items-center gap-1.5 text-xs text-slate-500">
          <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
          <span>
            Status reporting admins are assigned in Settings,{' '}
            {isAdmin
              ? <button type="button" onClick={() => navigate('/settings/admin')} className="font-semibold text-[#005EB8] hover:underline">Admin</button>
              : 'Admin'}.
          </span>
        </p>
      </div>
    </div>
  )
}
